import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'app/store/rootReducer';
import type {
  SearchAndFilterState,
  SetColumnFilterPayload,
  SetDateFilterPayload,
  SetExtraFilters,
  SetOnlyMyTransactions,
  SetSortOrderPayload,
} from 'util/filtering/models';
import {
  applyCardsColumnFilters,
  applyCardsSortFilter,
  executeCardsSearch,
} from 'app/store/filtering/cardsFiltersThunks';
import {
  applyTransactionColumnFilters,
  applyTransactionDateFilter,
  applyCodingTypeFilter,
  applySortFilter as applyTransactionSortFilter,
  clearTransactionColumnFilters,
  executeTransactionLoadMore,
  executeTransactionSearch,
} from 'app/store/filtering/transactionFiltersThunks';
import {
  applyUsersColumnFilters,
  applyUsersSortFilter,
  executeUsersLoadMore,
  executeUsersSearch,
  clearUsersColumnFilters,
} from 'app/store/filtering/userFiltersThunks';
import {
  applyCardGroupsSortFilter,
  applyCardGroupsColumnFilters,
  clearCardGroupsColumnFilters,
} from 'app/store/filtering/cardGroupFiltersThunks';
import {
  applyProjectGroupsSortFilter,
  applyProjectGroupsColumnFilters,
  clearProjectGroupsColumnFilters,
} from 'app/store/filtering/projectGroupFiltersThunks';

const createDefaultTransactionState = () => ({
  columnFilters: {},
  firstDate: undefined,
  lastDate: undefined,
  dateOption: undefined,
  sortOption: undefined,
  filtering: false,
});

const createDefaultCardsAdminState = () => ({
  columnFilters: {},
  sortOption: undefined,
  filtering: false,
});

// Group pages default to sorting by group name — the GSIs have no sort key, so without
// this the initial order is arbitrary. SEE-1163.
const createDefaultGroupsState = () => ({
  columnFilters: {},
  sortOption: { field: 'name', direction: 'asc' },
  filtering: false,
});

const initialState: SearchAndFilterState = {
  'profile-my-transactions': createDefaultTransactionState(),
  'overview-my-transactions': createDefaultTransactionState(),
  'overview-transactions': createDefaultTransactionState(),
  'overview-my-cards': createDefaultTransactionState(),
  'cards-admin': createDefaultCardsAdminState(),
  'cards-payment-cards': createDefaultCardsAdminState(),
  'profile-my-cards': createDefaultCardsAdminState(),
  'team-members': createDefaultCardsAdminState(),
  'team-card-groups': createDefaultGroupsState(),
  'team-project-groups': createDefaultGroupsState(),
};

const searchAndFilterSlice = createSlice({
  name: 'searchAndFilter',
  initialState,
  reducers: {
    setDateFilters: (state, action: PayloadAction<SetDateFilterPayload>) => {
      state[action.payload.page] = {
        ...state[action.payload.page],
        firstDate: action.payload.firstDate,
        lastDate: action.payload.lastDate,
        dateOption: action.payload.dateOption,
      };
    },
    setColumnFilters: (state, action: PayloadAction<SetColumnFilterPayload>) => {
      if (state[action.payload.pageName]) {
        state[action.payload.pageName].columnFilters = {
          ...state[action.payload.pageName]?.columnFilters,
          ...action.payload.columnFilters,
        };
      }
    },
    setSortOrder: (state, action: PayloadAction<SetSortOrderPayload>) => {
      state[action.payload.pageName] = {
        ...state[action.payload.pageName],
        sortOption: {
          ...state[action.payload.pageName]?.sortOption,
          ...action.payload.sortOption,
        },
      };
    },
    setExtraFilters: (state, action: PayloadAction<SetExtraFilters>) => {
      state[action.payload.pageName] = {
        ...state[action.payload.pageName],
        extraFilters: { ...action.payload.extraFilters },
      };
    },
    setOnlyMyTransactions: (state, action: PayloadAction<SetOnlyMyTransactions>) => {
      // Create the entry unconditionally (mirrors setSortOrder). A previous
      // `if (state[pageName])` guard silently dropped the flag for any page not
      // in initialState — which would reintroduce the unscoped-query leak this
      // flag exists to prevent if a new transaction page is added but not
      // registered. getFilterForPage tolerates the otherwise-partial entry
      // (columnFilters/filtering default via `|| {}` / `|| false`).
      state[action.payload.pageName] = {
        ...state[action.payload.pageName],
        onlyMyTransactions: action.payload.onlyMyTransactions,
      };
    },
    // SEE-974: the table "Transaction type" quick filter (All/GL/Project/Equipment).
    setCodingType: (state, action: PayloadAction<{ pageName: string; codingType: string }>) => {
      state[action.payload.pageName] = {
        ...state[action.payload.pageName],
        codingType: action.payload.codingType,
      };
    },
    setWaitingOnMe: (state, action: PayloadAction<{ pageName: string; waitingOnMe: boolean }>) => {
      state[action.payload.pageName] = {
        ...state[action.payload.pageName],
        waitingOnMe: action.payload.waitingOnMe,
      };
    },
    setSearchPageSize: (state, action: PayloadAction<{ pageName: string; pageSize: number }>) => {
      const { pageName, pageSize } = action.payload;
      const validPageSize = Number.isFinite(pageSize) && Number.isInteger(pageSize) && pageSize >= 1;
      if (state[pageName] && validPageSize) {
        state[pageName] = { ...state[pageName], pageSize };
      }
    },
    resetSearchAndFilter: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(clearTransactionColumnFilters.pending, (state, action) => {
      const filtersToClear = {
        ...(state[action.meta.arg.pageName]?.columnFilters || {}),
      };
      Object.keys(filtersToClear).forEach((key) => {
        filtersToClear[key].selectedValues = [];
        filtersToClear[key].rule = filtersToClear[key].type === 'number' ? 'range' : 'noExcept';
      });
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        columnFilters: filtersToClear,
        filtering: true,
      };
    });
    builder.addCase(applyTransactionDateFilter.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        firstDate: action.meta.arg.firstDate ? action.meta.arg.firstDate : undefined,
        lastDate: action.meta.arg.lastDate ? action.meta.arg.lastDate : undefined,
        dateOption: action.meta.arg.dateOption,
        filtering: true,
      };
    });
    builder.addCase(applyTransactionSortFilter.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        sortOption: action.meta.arg.sortOption,
        filtering: true,
      };
    });
    // SEE-974: coding-type filter drives the same loading skeleton as the other
    // transaction filters. The thunk dispatches setCodingType itself, so we only
    // toggle `filtering` here. (fulfilled below, rejected in the shared list.)
    builder.addCase(applyCodingTypeFilter.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: true,
      };
    });
    builder.addCase(applyTransactionColumnFilters.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        columnFilters: action.meta.arg.columnFilters,
        filtering: true,
      };
    });
    builder.addCase(applyCardsColumnFilters.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        columnFilters: action.meta.arg.columnFilters,
        sortOption: undefined, // Clear sort when filters change
        filtering: true,
      };
    });
    builder.addCase(applyCardsSortFilter.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        sortOption: action.meta.arg.sortOption,
        filtering: true,
      };
    });
    builder.addCase(applyUsersColumnFilters.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        columnFilters: action.meta.arg.columnFilters,
        sortOption: undefined, // Clear sort when filters change
        filtering: true,
      };
    });
    builder.addCase(applyUsersSortFilter.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        sortOption: action.meta.arg.sortOption,
        filtering: true,
      };
    });
    builder.addCase(applyCardGroupsSortFilter.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        sortOption: action.meta.arg.sortOption,
        filtering: true,
      };
    });
    builder.addCase(applyCardGroupsColumnFilters.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: true,
      };
    });
    builder.addCase(clearCardGroupsColumnFilters.pending, (state, action) => {
      const filtersToClear = {
        ...(state[action.meta.arg.pageName]?.columnFilters || {}),
      };
      Object.keys(filtersToClear).forEach((key) => {
        filtersToClear[key].selectedValues = [];
        filtersToClear[key].rule = filtersToClear[key].type === 'number' ? 'range' : 'noExcept';
      });
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        columnFilters: filtersToClear,
        filtering: true,
      };
    });
    builder.addCase(applyProjectGroupsSortFilter.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        sortOption: action.meta.arg.sortOption,
        filtering: true,
      };
    });
    builder.addCase(applyProjectGroupsColumnFilters.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: true,
      };
    });
    builder.addCase(clearProjectGroupsColumnFilters.pending, (state, action) => {
      const filtersToClear = {
        ...(state[action.meta.arg.pageName]?.columnFilters || {}),
      };
      Object.keys(filtersToClear).forEach((key) => {
        filtersToClear[key].selectedValues = [];
        filtersToClear[key].rule = filtersToClear[key].type === 'number' ? 'range' : 'noExcept';
      });
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        columnFilters: filtersToClear,
        filtering: true,
      };
    });
    builder.addCase(executeUsersLoadMore.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: true,
      };
    });
    builder.addCase(executeUsersSearch.pending, (state, action) => {
      state[action.meta.arg] = {
        ...state[action.meta.arg],
        filtering: true,
      };
    });
    builder.addCase(clearUsersColumnFilters.pending, (state, action) => {
      const filtersToClear = {
        ...(state[action.meta.arg.pageName]?.columnFilters || {}),
      };
      Object.keys(filtersToClear).forEach((key) => {
        filtersToClear[key].selectedValues = [];
        filtersToClear[key].rule = filtersToClear[key].type === 'number' ? 'range' : 'noExcept';
      });
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        columnFilters: filtersToClear,
        filtering: true,
      };
    });
    builder.addCase(executeTransactionLoadMore.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: true,
      };
    });
    builder.addCase(executeTransactionSearch.pending, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: true,
      };
    });
    builder.addCase(clearTransactionColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyTransactionDateFilter.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyCodingTypeFilter.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyTransactionSortFilter.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyTransactionColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyCardsSortFilter.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyCardsColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(executeTransactionLoadMore.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(executeTransactionSearch.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(executeCardsSearch.fulfilled, (state, action) => {
      state[action.meta.arg] = {
        ...state[action.meta.arg],
        filtering: false,
      };
    });
    builder.addCase(applyUsersColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyUsersSortFilter.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(executeUsersLoadMore.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(executeUsersSearch.fulfilled, (state, action) => {
      state[action.meta.arg] = {
        ...state[action.meta.arg],
        filtering: false,
      };
    });
    builder.addCase(clearUsersColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyCardGroupsSortFilter.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyCardGroupsColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(clearCardGroupsColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyProjectGroupsSortFilter.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(applyProjectGroupsColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });
    builder.addCase(clearProjectGroupsColumnFilters.fulfilled, (state, action) => {
      state[action.meta.arg.pageName] = {
        ...state[action.meta.arg.pageName],
        filtering: false,
      };
    });

    // Add rejected cases for error handling
    const rejectedCases = [
      clearTransactionColumnFilters.rejected,
      applyTransactionDateFilter.rejected,
      applyCodingTypeFilter.rejected,
      applyTransactionSortFilter.rejected,
      applyTransactionColumnFilters.rejected,
      applyCardsColumnFilters.rejected,
      applyCardsSortFilter.rejected,
      executeCardsSearch.rejected,
      executeTransactionLoadMore.rejected,
      executeTransactionSearch.rejected,
      applyUsersColumnFilters.rejected,
      applyUsersSortFilter.rejected,
      executeUsersLoadMore.rejected,
      executeUsersSearch.rejected,
      clearUsersColumnFilters.rejected,
      applyCardGroupsSortFilter.rejected,
      applyCardGroupsColumnFilters.rejected,
      clearCardGroupsColumnFilters.rejected,
      applyProjectGroupsSortFilter.rejected,
      applyProjectGroupsColumnFilters.rejected,
      clearProjectGroupsColumnFilters.rejected,
    ];

    rejectedCases.forEach((thunk) => {
      builder.addCase(thunk, (state, action) => {
        const pageName = typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg.pageName;
        if (pageName && state[pageName]) {
          state[pageName].filtering = false;
        }
      });
    });
  },
});

export const {
  setDateFilters,
  setColumnFilters,
  setSortOrder,
  setExtraFilters,
  setOnlyMyTransactions,
  setCodingType,
  setWaitingOnMe,
  setSearchPageSize,
  resetSearchAndFilter,
} = searchAndFilterSlice.actions;

export const selectSearchAndFilter = (state: RootState): SearchAndFilterState => state.searchAndFilter;

export default searchAndFilterSlice.reducer;
